import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { useProjectManager } from '../hooks/useProjectManager';
import { Task, ProjectMetadata } from '../types';
import { canUserMoveTaskBetweenProjects } from '../utils/projectPermissions';

type ProjectManagerReturnType = ReturnType<typeof useProjectManager>;

interface ProjectContextType extends ProjectManagerReturnType {
  // Expose computed helper values
  allTasksGlobal: Task[];
  globalMetadata: ProjectMetadata;
  handleAssignTaskToProject: (taskId: string | number, newProjectId: string) => Promise<void>;
}

export const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

const EMPTY_METADATA: ProjectMetadata = {};
const projectTasksCache = new WeakMap< object, Task[] >();

export const ProjectProvider: React.FC<{ userId?: string; userEmail?: string; children: React.ReactNode }> = ({ userId, userEmail, children }) => {
  const state = useProjectManager(userId);

  // Compute global helpers inside the context provider with reference-stable caching per project (Schritt 4.2)
  const allTasksGlobal = useMemo(() => {
    return state.allProjects.flatMap(p => {
      const cached = projectTasksCache.get(p);
      if (cached) return cached;
      const mapped = (p.tasks || []).map(t => (t.projectId === p.id ? t : { ...t, projectId: p.id }));
      projectTasksCache.set(p, mapped);
      return mapped;
    });
  }, [state.allProjects]);

  const globalMetadata = useMemo(() => {
    return state.allProjects.find(p => p.id === 'inbox_inforum')?.metadata || EMPTY_METADATA;
  }, [state.allProjects]);

  const { allProjects, reorderTask, addLogEntry } = state;

  // Expose task assignment logic globally
  const handleAssignTaskToProject = useCallback(async (taskId: string | number, newProjectId: string) => {
    const task = allTasksGlobal.find(t => 
      String(t.id).trim() === String(taskId).trim() || 
      String(taskId).trim() === `${t.projectId}_${t.id}`
    );
    
    if (!task) {
      console.warn(`[ProjectContext] handleAssignTaskToProject: Task ${taskId} not found.`, { taskId });
      return;
    }

    if (
      !canUserMoveTaskBetweenProjects(
        task,
        newProjectId,
        allProjects,
        userId,
        userEmail || state.user?.email
      )
    ) {
      console.warn(`[ProjectContext] handleAssignTaskToProject blocked by RBAC for task ${taskId} -> ${newProjectId}`);
      return;
    }
    
    const realTaskId = task.id;
    reorderTask(realTaskId, newProjectId, task.status || 'todo', undefined, 'end', 'status', undefined, undefined, task.projectId);
    
    const targetProjectName = allProjects.find(p => p.id === newProjectId)?.name || (newProjectId === 'inbox_inforum' ? 'Nicht zugeordnet' : 'Unbekannt');
    addLogEntry('update', `Aufgabe "${task.name}" dem Projekt "${targetProjectName}" zugewiesen`);
  }, [allTasksGlobal, allProjects, userId, userEmail, state.user?.email, reorderTask, addLogEntry]);

  const value = useMemo(() => ({
    ...state,
    allTasksGlobal,
    globalMetadata,
    handleAssignTaskToProject
  }), [state, allTasksGlobal, globalMetadata, handleAssignTaskToProject]) as ProjectContextType;

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};
